<template>
  <div
    class="BuyBox"
    :class="{
      'mobOnly:mb-48': design !== 'quickbuy',
    }"
  >
    <ConfirmModal
      v-if="showConfirmAddToCart"
      :show-abort="true"
      @close="showConfirmAddToCart = false"
      @click-outside="showConfirmAddToCart = false"
      @confirm="addToCart"
    >
      <div class="p-32 pb-24 type-lg">
        {{ $t('productPage.buybox.confirmBackorderTitle', { date: estimatedDeliveryDate }) }}
      </div>
      <div class="wysiwyg pr-32 pl-24 pb-32">
        <ul>
          <li>
            {{ $t('productPage.buybox.confirmBackorderContentFirst') }}
          </li>
          <li>
            {{ $t('productPage.buybox.confirmBackorderContentSecond') }}
          </li>
        </ul>
      </div>
    </ConfirmModal>

    <!-- old norce discount variant -->
    <MultiBuyDiscount
      :key="activeVariant.partNo"
      :active-variant="activeVariant"
      @change-campaign-type="changeCampaignType"
    />

    <!-- new quantity-breaks api -->
    <ShowDiscountQuantityBreaks
      v-if="userStore.isLoggedIn"
      :discount-quantity-breaks="discountQuantityBreaks"
      :categories="productItem.categories"
    />

    <div v-if="!activeVariant.productHasExpired" class="type-label-sm text-text-base">
      <div
        class="mb-8"
        :class="{
          'hidden': disabledQuantityBtns && !shouldUseArea,
        }"
      >
        {{ $t('productPage.sizeSelector.quantityLabel') }}
      </div>

      <!-- * * * * * if product should be calculated in length/area -->
      <PackageToAreaConverter
        v-if="shouldUseArea"
        v-model="totalQuantity"
        :input="activeVariant"
      />

      <!-- * * * * * standard quantity selector -->
      <div v-else-if="!disabledQuantityBtns" class="flex justify-left gap-8 desk:gap-12 items-end select-none">
        <div class="inline-flex h-40">
          <button
            class="h-40 w-40 basis-40 inline-flex items-center justify-center shrink-0 grow-0 rounded-l-2 border text-text-subtle hover:text-text-base border-border-subtle group hover:border-border-strong relative transition-colors"
            :class="{ 'pointer-events-none': totalQuantity === 1}"
            :disabled="totalQuantity === subOrAddQuantity"
            @click="totalQuantity = (totalQuantity - subOrAddQuantity)"
          >
            <GlobalIcon name="minus" size="xsmall" />
          </button>
          <div class="border border-border-subtle -ml-1 hover:border-border-strong hover:relative transition-colors w-[52px] basis-[52px]">
            <input
              v-model.number="totalQuantity"
              type="number"
              class="w-full h-full text-center text-text-base type-sm leading-single rounded-0 quantity-input"
              min="1"
            >
          </div>
          <button
            class="h-40 w-40 basis-40 shrink-0 grow-0 -ml-1 inline-flex items-center justify-center border border-border-subtle text-text-subtle hover:text-text-base rounded-r-2 group hover:border-border-strong hover:relative transition-colors"
            :class="{ 'pointer-events-none': totalQuantity >= canBuyMoreAmount && canBuyMoreAmount > 0 || canBuyMoreAmount === 0 && cartStore.getQuantityFromPartNo(activeVariant.partNo) > 0 }"
            :disabled="!globalContent.getAllowBackorders && totalQuantity + subOrAddQuantity > canBuyMoreAmount && !activeVariant.isOnDemandProduct"
            @click="totalQuantity = (totalQuantity + subOrAddQuantity)"
          >
            <GlobalIcon name="plus" size="xsmall" />
          </button>
        </div>
        <div
          v-for="bulkOption in [6, 12, 24]"
          :key="'bulk-option-' + bulkOption"
          class="w-auto h-40 shrink-0 type-headline-sm"
        >
          <button
            class="btn btn--outline"
            :class="{'pointer-events-none': !globalContent.getAllowBackorders && !activeVariant.isOnDemandProduct && !activeVariant.isPreOrderProduct && canBuyMoreAmount < bulkOption}"
            :disabled="!globalContent.getAllowBackorders && !activeVariant.isOnDemandProduct && !activeVariant.isPreOrderProduct && canBuyMoreAmount < bulkOption"
            @click="totalQuantity = (totalQuantity = bulkOption)"
          >
            +{{ bulkOption }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="globalContent.getPurchasesEnabled">
      <!-- "can not add more in cart" -->
      <div v-if="!productItem.shouldCheckFutureAvailability && !globalContent.getAllowBackorders && !activeVariant.isOnDemandProduct" class="type-sm-medium mt-8">
        <!-- already on max -->
        <div
          v-if="canBuyMoreAmount === 0 && cartStore.getQuantityFromPartNo(activeVariant.partNo) > 0"
          class="inline-flex relative p-12 items-center gap-6 bg-warningLight"
        >
          <img
            src="/icons/info.svg"
            class="w-16 h-16"
            alt="info"
          >
          {{ $t('stock.maxInCart') }}
        </div>
        <!-- more than max -->
        <div
          v-else-if="totalQuantity >= canBuyMoreAmount && canBuyMoreAmount > 0"
          class="inline-flex relative p-12 items-center gap-6 bg-warningLight"
        >
          <div class="w-16 h-16 rounded-1 rotate-45 bg-warningLight absolute -top-4 left-48" />
          <img
            src="/icons/info.svg"
            class="w-16 h-16"
            alt="info"
          >
          {{ $t('stock.maxCanAdd', { num: canBuyMoreAmount }) }}
        </div>
      </div>
    </div>
    <div class="flex items-center flex-wrap mt-8 mb-16 min-h-20">
      <div class="items-center type-sm mr-16">
        <StockStatusSwitcher
          :total-quantity="totalQuantity"
          :product-item="productItem"
          :active-variant="activeVariant"
          @loading="e => loadingFromFutureStockApi = e"
          @set-buy-button-disabled="setDisableBuyFromFutureStockApi"
        />
      </div>

      <div v-if="shouldUseArea && showWasteDisclaimer" class="flex items-center gap-6 mt-4">
        <div class="inline-flex items-center justify-center w-[14px] h-[14px] shrink-0 place-self-start relative translate-y-2 desk:translate-y-3">
          <GlobalIcon name="info" size="xsmall" />
        </div>
        <div class="type-sm">{{ $t('areaConvert.disclaimerShort') }}</div>
      </div>

      <!-- Area / Quantity per pallet
      <div v-if="palletQuantityInfo && globalContent.getCountryCode !== 'us'" class="type-sm flex items-center">
        <img
          src="/icons/package.svg"
          class="w-16 h-16 mr-6"
          alt="info"
        >
        {{ $t('productPage.pallet') }}: {{ palletQuantityInfo.area }} / {{ palletQuantityInfo.packages }}
      </div> -->
    </div>

    <!-- * * * * notify when back in stock -->
    <div v-if="!globalContent.getAllowBackorders && activeVariant && !activeVariant.canAddToCart && !activeVariant.productHasExpired && globalContent.getPurchasesEnabled">
      <div v-if="!stockAlertSuccess" class="relative h-48 mb-12">
        <TextInput
          v-model="outOfStockEmail"
          type="email"
          class="input h-48"
          :label="$t('productPage.stockAlertPlaceholder')"
          @keyup.enter="subscribeToStockUpdates"
        />
      </div>
      <div v-if="stockAlertError" class="text-criticalDark text-14 mb-12">
        {{ $t('productPage.stockAlertError') }}
      </div>
      <div
        v-if="stockAlertSuccess"
        class="type-headline-sm text-center mb-12 p-24"
        :class="{
          'bg-successPrimary text-lightest': lastResponseCode !== 200,
          'bg-lighter': lastResponseCode === 200}"
      >
        {{ stockAlertSuccessText ?? $t('productPage.stockAlertSuccess') }}
      </div>
    </div>

    <!-- * * * * active product -->
    <div v-else>
      <div v-if="globalContent.getEnableOrderProposalRequest && showOrderProposalRequest && userStore.isLoggedIn && !userStore.isSalesRepUser" class="mb-16 w-full">
        <button
          class="btn btn--lg btn--secondary w-full mb-8"
          :class="{
            'loading': requestProposalLoading,
          }"
          @click="onClickRequestOrderProposal"
        >
          {{ $t('productPage.requestOrderProposalButton') }}
        </button>
        <div class="flex desk:items-center justify-center">
          <img
            src="/icons/info-subtle.svg"
            class="w-16 h-16 mr-6"
            alt="info"
          >
          <p class="type-xs text-dark leading-single">{{ $t('productPage.requestOrderProposalDescription') }}</p>
        </div>
      </div>
      <div v-if="!stockAlertSuccess" class="flex">
        <button
          v-if="!globalContent.getPurchasesEnabled"
          class="btn btn--lg flex-1 w-full btn"
          @click.prevent="showLogin"
        >
          {{ $t('priceInfo.loginForPriceInfo') }}
        </button>
        <!-- logged in but cant be added to cart -->
        <button
          v-else-if="!globalContent.getAllowBackorders && !props.activeVariant.canAddToCart"
          class="btn btn--lg flex-1 w-full mb-24 btn--secondary"
          :disabled="!validNotifyMail"
          @click="subscribeToStockUpdates"
        >
          {{ $t('notify.openModalButton') }}
        </button>

        <Tooltip
          v-else-if="userStore.isFinanceUser"
          :show-on-hover="true"
          class="ml-8 w-full"
          :text="$t('priceInfo.financeUserTooltip')"
        >
          <button
            class="btn btn--lg flex-1 w-full mb-24 btn"
            :class="{
              'pointer-events-none': true,
            }"
            :disabled="true"
          >
            {{ addToCartLabel }}
            <span
              v-if="totalSum > 0"
              class="ml-8 text-12 normal-case"
            >
              {{ useLocalizedCurrency(totalSum) }}
            </span>
          </button>
        </Tooltip>

        <!-- * * * * actual buy button -->
        <button
          v-else
          class="btn btn--secondary btn--lg flex-1 w-full btn"
          :class="{
            'loading': loading || loadingFromFutureStockApi,
            'pointer-events-none': disabledBuyButton,
          }"
          :disabled="disabledBuyButton || disableBuyFromFutureStockApi"
          @click="maybeConfirmAddToCart"
        >
          <!-- <GlobalIcon name="cart" size="small"/> -->
          <span class="ml-8">{{ addToCartLabel }}</span>
          <span
            v-if="totalSum > 0"
            class="ml-6"
          >–
          </span>
          <span
            v-if="totalSum > 0"
            class="ml-6"
          >
            {{ useLocalizedCurrency(totalSum) }}
          </span>
          <span
            v-if="totalSum > 0 && reachedDiscountPercent > 0"
            class="ml-8 opacity-50 line-through"
          >
            {{ useLocalizedCurrency(totalBasePrice) }}
          </span>
        </button>

        <!-- * * * * favorite button -->
        <button
          v-if="userStore.canFavorizeProducts"
          class="w-48 h-48 px-12 ml-12 shrink-0 border hover:bg-lightest hover:border-darkest transition-all select-none"
          :class="{
            'bg-bg-base border-border-inverse' : !isFavorite,
            'bg-lightest border border-light' : isFavorite,
          }"
          @click="addOrDeleteFavorite"
        >
          <img
            alt=""
            :src="showStar ? '/icons/heart-filled.svg' : '/icons/heart.svg'"
            class="h-24 w-24"
            :class="{
              'favoriteAnimateIn': loadingToTrue,
            }"
          >
        </button>
      </div>

      <!-- Request installation quote -->
      <template v-if="hasRequestInstallation">
        <div class="w-full flex items-center justify-center gap-16 my-16">
          <div class="bg-border-subtle w-full h-1" />
          <span class="type-tagline-xs text-text-subtle whitespace-nowrap">{{ $t('productPage.installation.divider') }}</span>
          <div class="bg-border-subtle w-full h-1" />
        </div>
        <div>
          <button
            class="btn btn--outline w-full btn--lg"
            @click="scrollToAnchor('RequestInstallation')"
          >
            {{ $t('productPage.installation.cta') }}
          </button>
          <div class="flex p-16 gap-8 bg-bg-brandSubtle mt-16 items-start rounded-2">
            <div class="inline-block shrink-0 text-text-base">
              <GlobalIcon name="SealPercent" size="small" />
            </div>
            <div>
              <div class="type-label-sm text-text-base my-6">{{ $t('productPage.installation.title') }}</div>
              <div class="type-sm text-text-subtle">{{ $t('productPage.installation.description') }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- * * * * order samples
    Don't show samples if we are already on a sample page
    -->
    <div v-if="hasSampleRelations && globalContent.getIsB2b && !activeVariant.partNo?.startsWith('SA0')" class="mt-16">
      <div class="w-full flex items-center justify-center gap-16 mb-16">
        <div class="bg-border-subtle w-full h-1" />
        <span class="type-tagline-xs text-text-subtle">Or</span>
        <div class="bg-border-subtle w-full h-1" />
      </div>
      <button
        class="btn btn--secondary w-full btn--lg"
        :class="{ loading: isLoadingSamples }"
        :disabled="isLoadingSamples"
        @click="openSampleQuickbuy"
      >
        {{ $t('samples.cta') }}
      </button>
      <transition name="fromRight">
        <QuickBuyMultiple
          v-if="showSampleQuickbuy"
          :products-in="samplesHydrated"
          category-code="Samples"
          @close="showSampleQuickbuy=false"
        />
      </transition>
    </div>

    <!-- * * * * disclaimer about meter/m2 -->
    <div
      v-if="globalContent.getIsB2b && shouldUseArea && design !== 'quickbuy' && showWasteDisclaimer"
      class="flex p-16 gap-8 bg-bg-brandSubtle type-sm mt-24 items-start rounded-2"
    >
      <div class="inline-block shrink-0">
        <GlobalIcon name="info" size="small" />
      </div>
      <div>{{ $t('areaConvert.disclaimer') }}</div>
    </div>

    <RequestOrderProposalModal
      v-if="showRequestOrderProposalModal"
      @close="showRequestOrderProposalModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import Product, { ProductModel } from '~/models/product';
import { useCartStore } from '~/store/cart';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import { useProductStore } from '~/store/product';
import { useUiStore } from '~/store/ui';
import ProductRelatedProductsList from '~/components/product-page/ProductRelatedProductsList.vue';
import ProductVariant, { ProductVariantModel } from '~/models/productVariant';
import useApiFetch from '~/composeables/useApiFetch';
import { NorceProductTypes, NorceRelatedProductTypeCodes } from '~/constants/norceCodes';
import MultiBuyDiscount from '~/components/product-page/MultiBuyDiscount.vue';
import Tooltip from '~/components/body/Tooltip.vue';
import { CampaignItem } from '~/constants/types/multiDiscount';
import useMultiDiscount from '~/composeables/useMultiDiscount';
import TextInput from '~/components/form-elements/TextInput.vue';
import ConfirmModal from '~/components/modals/ConfirmModal.vue';
import usePackageAreaConverter from '~/composeables/usePackageAreaConverter';
import PackageToAreaConverter from '~/components/product-page/PackageToAreaConverter.vue';
import { DiscountQuantityBreak, DiscountQuantityBreakApiResponse } from '~/constants/types/norce';
import NP from 'number-precision';
import ShowDiscountQuantityBreaks from '~/components/product-page/ShowDiscountQuantityBreaks.vue';
import { useLocalizedCurrency } from '~/composeables/useLocalizedCurrency';
import QuickBuyMultiple from '~/components/product/QuickBuyMultiple.vue';
import useAlgoliaFetch from '~/composeables/useAlgoliaFetch';
import StockStatusSwitcher from '~/components/product/StockStatusSwitcher.vue';
import RequestOrderProposalModal from '~/components/modals/RequestOrderProposalModal.vue';
import useWarehouse from '~/composeables/useWarehouse';

NP.enableBoundaryChecking(false);

const { apiGet } = useApiFetch();

const userStore = useUserStore();
const uiStore = useUiStore();
const cartStore = useCartStore();
const productStore = useProductStore();
const globalContent = useGlobalContentStore();
const { getVariantStockValue } = useWarehouse();
const { $t } = useNuxtApp();
const totalQuantity = ref(1);
const totalQuantityChanged = ref(false); // if user changed totalquantity
const subOrAddQuantity = ref(1);
const relatedList = ref<ProductModel[]>([]);
const { getExactVariantByPartNo, lastError, lastErrorCode } = useAlgoliaFetch();
const showRequestOrderProposalModal = ref(false);

interface Props {
  activeVariant: ProductVariantModel & { name: string },
  productItem: ProductModel,
  disabled?: boolean
  crossell?: boolean,
  design: 'standard' |'quickbuy',
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  crossell: false,
  design: 'standard',
});

const {
  percentageCampaigns,
  hasCampaign,
  giftCampaigns,
} = useMultiDiscount({ activeVariant: props.activeVariant });

const {
  unitDisplay,
  shouldUseArea,
  areaPerPackage,
  convertPackagePriceToAreaPrice,
} = usePackageAreaConverter(toRef(()=>props.activeVariant));

const priceListLocked = ref(hasCampaign && !giftCampaigns.value.length);

const disabledQuantityBtns = computed(() => {
  return (!userStore.isLoggedIn || props.activeVariant.isSoldOut)
    && !props.activeVariant.isOnDemandProduct
    && !props.activeVariant.isPreOrderProduct
    && !globalContent.getAllowBackorders
    && !props.productItem.shouldCheckFutureAvailability;
});

const canBuyMoreAmount = computed<number>(()=> {
  if (props.productItem.shouldCheckFutureAvailability) {
    return Infinity;
  }
  const cartQty = cartStore.getQuantityFromPartNo(props.activeVariant.partNo);
  const stockValue = getVariantStockValue(props.activeVariant);
  const qty = stockValue - cartQty;

  if (props.activeVariant.hasFixedRecommendedQty && (props.activeVariant.recommendedQty && qty > props.activeVariant.recommendedQty)) {
    if ((cartQty + totalQuantity.value) > props.activeVariant.recommendedQty) {
      return 0;
    }
    return props.activeVariant.recommendedQty;
  }

  return qty;
});

const disabledBuyButton = computed(() => {
  if (userStore.isFinanceUser) {
    return true;
  } else if (props.disabled) {
    return true;
  } else if (props.productItem.productUnavailable) {
    return true;
  } else if (globalContent.getAllowBackorders) {
    return false;
  } else if ((!props.activeVariant.isInStock || props.activeVariant.isSoldOut) && !props.activeVariant.isOnDemandProduct) {
    return true;
  } else if (props.activeVariant.isInStock || props.activeVariant.isPreOrderProduct || props.activeVariant.isOnDemandProduct || props.activeVariant.hasIncomingDate || props.activeVariant.isSoldOut) {
    /*console.group();
    console.log('isInStock: ' +props.activeVariant.isInStock);
    console.log('isPreOrderProduct: ' +props.activeVariant.isPreOrderProduct);
    console.log('isOnDemandProduct: ' +props.activeVariant.isOnDemandProduct);
    console.log('hasIncomingDate: ' +props.activeVariant.hasIncomingDate);
    console.log('isSoldOut: ' +props.activeVariant.isSoldOut);
    console.groupEnd();*/
    return false;
  }

  return true;
});

const addToCartLabel = computed(() => {
  if (globalContent.getAllowBackorders || props.activeVariant.shouldCheckFutureAvailability) {
    return $t('productPage.sizeSelector.addToCartBtn'); // add to cart
  } else if (props.activeVariant.isPreOrderProduct) {
    return $t('productPage.sizeSelector.preOrder'); // preorder
  } else if (props.activeVariant.isTempSoldOut) {
    return $t('productPage.sizeSelector.watchStockBtn'); // add to watch
  } else if (props.activeVariant.isOnDemandProduct || (props.activeVariant && props.activeVariant.canAddToCart) || props.activeVariant.hasIncomingDate) {
    return $t('productPage.sizeSelector.addToCartBtn'); // add to cart
  }
  return $t('productPage.sizeSelector.watchStockBtn'); // add to watch
});

const totalSum = computed((): number => {
  if ((!globalContent.getAllowBackorders && props.activeVariant.canAddToCart) || (globalContent.getAllowBackorders && !props.activeVariant.productHasExpired)) {
    if (userStore.getPrice(props.activeVariant.partNo, props.crossell).status === 'ok') {
      const basePrice = priceListLocked.value
        ? Number(userStore.getPrice(props.activeVariant.partNo, props.crossell).price?.priceBeforeDiscount) ?? 0
        : Number(userStore.getPrice(props.activeVariant.partNo, props.crossell).price?.price) ?? 0;

      // discounts from api
      if (reachedDiscountPrice.value) {
        return totalQuantity.value * reachedDiscountPrice.value;
      }
      // discounts from norce
      else if (percentageCampaigns.value.length > 0) {
        const highestLimitReachedTrue = percentageCampaigns.value
          .filter(item => totalQuantity.value >= item.limit)
          .reduce<CampaignItem | null>((acc, item) => {
            return !acc || item.limit > acc.limit ? item : acc;
          }, null);

        return highestLimitReachedTrue ? totalQuantity.value * basePrice * ((100 - highestLimitReachedTrue.gainedAmount) / 100) : totalQuantity.value * basePrice;
      } else {
        return totalQuantity.value * basePrice;
      }
    }
  }

  return 0;
});

// Price * Qty without any discount
const totalBasePrice = computed((): number => {
  if ((!globalContent.getAllowBackorders && props.activeVariant.canAddToCart) || (globalContent.getAllowBackorders && !props.activeVariant.productHasExpired)) {
    if (userStore.getPrice(props.activeVariant.partNo, props.crossell).status === 'ok') {
      const basePrice = priceListLocked.value
        ? Number(userStore.getPrice(props.activeVariant.partNo, props.crossell).price?.priceBeforeDiscount) ?? 0
        : Number(userStore.getPrice(props.activeVariant.partNo, props.crossell).price?.price) ?? 0;

      return totalQuantity.value * basePrice;
    }
  }

  return 0;
});

const showOrderProposalRequest = computed(() => {
  const currentProductArea = areaPerPackage.value ? (areaPerPackage.value * totalQuantity.value) : 0;
  const totalArea = cartStore.cartTotalArea + currentProductArea;

  return totalArea >= globalContent.orderProposalRequestAreaThreshold;
});

const showWasteDisclaimer = computed(() => {
  return props.productItem.categories?.some(category => category.code?.toLowerCase() === 'floors') || false;
});

const showLogin = () => {
  uiStore.setShowSigninModal(true, true);
};

const emit = defineEmits<{
  (e: 'changeSize', size: ProductVariant & { name: string }): void;
  (e: 'addToCart'): void;
  (e: 'addingToCart'): void;
}>();

const outOfStockEmail = userStore.isLoggedIn ? ref(userStore.userProfile.email) : ref('');

const { apiPost, lastResponseCode } = useApiFetch();
const loadingStockAlert = ref(false);
const stockAlertSuccess = ref(false);
const stockAlertSuccessText = ref<string | null>(null);
const stockAlertError = ref(false);
const subscribeToStockUpdates = async() => {
  if (validNotifyMail.value) {
    loadingStockAlert.value = true;
    stockAlertError.value = false;
    stockAlertSuccess.value = false;
    stockAlertSuccessText.value = null;
    const res = await apiPost(`/products/${props.activeVariant.partNo}/stock-alerts`, {
      email: outOfStockEmail.value,
    });
    if (res) {
      stockAlertSuccess.value = true;
      if (lastResponseCode.value === 200) {
        stockAlertSuccessText.value = $t('productPage.stockAlertSuccessAgain');
      }

      setTimeout(()=> stockAlertSuccess.value = false, 10000);
    } else {
      stockAlertError.value = true;
    }

    loadingStockAlert.value = false;
  } else {
    console.warn('not valid');
  }
};

const validNotifyMail = computed(()=> {
  const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  return regex.test(outOfStockEmail.value);
});

const loading = ref(false);
// Same as loading but while future stock api
const loadingFromFutureStockApi = ref(false);
const showConfirmAddToCart = ref(false);

// If we have an expiring product, that wan't return, we disable buy button
const disableBuyFromFutureStockApi = ref(false);
const setDisableBuyFromFutureStockApi = (set: boolean) => {
  if (props.activeVariant.isExpiring) {
    disableBuyFromFutureStockApi.value = set;
  }
};

const maybeConfirmAddToCart = async() => {
  if (isBackorderProduct.value) {
    showConfirmAddToCart.value = true;
  } else {
    await addToCart();
    // Scroll to ProductAccessories
    if (props.design === 'standard') {
      const section = document.querySelector('.ProductAccessories');
      if (section) {
        const offset = 88;
        const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  }
};

const addToCart = async() => {
  loading.value = true;
  emit('addingToCart');

  if (
    (!globalContent.getAllowBackorders &&
          (props.activeVariant.canAddToCart || props.activeVariant.hasIncomingDate)) ||
      (globalContent.getAllowBackorders && !props.activeVariant.productHasExpired)
  ) {
    await cartStore.updateCart(
      props.activeVariant.partNo,
      totalQuantity.value,
      -1,
      !uiStore.quickBuyCloseAfterAdd,
      props.activeVariant,
      false,
      props.productItem.objectID,
      priceListLocked.value
    );
    emit('addToCart');
  }

  loading.value = false;
};

const loadingToTrue = ref(false);
const loadingToFalse = ref(false);

const addOrDeleteFavorite = async() => {
  if (isFavorite.value) {
    loadingToFalse.value = true;
    await userStore.updateFavoriteProductQuantity(props.activeVariant.partNo, 0);
    loadingToFalse.value = false;
  } else {
    loadingToTrue.value = true;
    await userStore.saveFavoriteProduct(props.activeVariant.partNo, totalQuantity.value);
    loadingToTrue.value = false;
  }
};

const showStar = computed(()=> {
  return (isFavorite.value && !loadingToFalse.value) || loadingToTrue.value;
});

const isFavorite = computed((): boolean => {
  return !!userStore.userProfile.favoriteProducts?.find(favorite => favorite.id === props.activeVariant.partNo);
});

const isBackorderProduct = computed(() => {
  return globalContent.getAllowBackorders && !props.activeVariant.isInStock;
});

const estimatedDeliveryDate = computed(() => {
  return props.activeVariant.incomingDate;
});

watch(
  () => props.activeVariant,
  () => {
    totalQuantity.value = 1;
    totalQuantityChanged.value = false;
    stockAlertError.value = false;
    stockAlertSuccess.value = false;
    if (props.activeVariant.productHasExpired) {
      setRelatedList();
    }
  }
);

onMounted(() => {
  if (props.activeVariant.productHasExpired) {
    setRelatedList();
  }
});

const setRelatedList = (async() => {
  const partNos = props.productItem.relatedProductsByType(NorceRelatedProductTypeCodes.Replacer);
  if (partNos.length > 0) {
    relatedList.value = await productStore.getRelatedProducts(partNos);
  }
});

const changeCampaignType = (type: boolean) => {
  if (!giftCampaigns.value.length) {
    priceListLocked.value = type;
  }
};

const requestProposalLoading = ref(false);
const onClickRequestOrderProposal = async() => {
  requestProposalLoading.value = true;
  await cartStore.updateCart(
    props.activeVariant.partNo,
    totalQuantity.value,
    -1,
    false,
    props.activeVariant,
    false,
    props.productItem.objectID,
    priceListLocked.value
  );
  // Open order proposal modal
  showRequestOrderProposalModal.value = true;
  requestProposalLoading.value = false;
};

watch(totalQuantity, () => {
  totalQuantityChanged.value = true;
});

watch(() => props.activeVariant, ()=> {
  checkDiscountLevels();
});

// * * * * * * * * checking quantity breaks discounts
const discountCBraw = ref<DiscountQuantityBreakApiResponse[]>([]);
const checkDiscountLevels = async() => {
  const res = await apiGet<DiscountQuantityBreakApiResponse[]>(`/products/${props.activeVariant.partNo}/quantity-breaks`);
  if (res) {
    discountCBraw.value = res;
  } else {
    console.warn(`Couldn't load quantity-breaks on ${props.activeVariant.partNo}`);
  }
};

const discountQuantityBreaks = computed<DiscountQuantityBreak[]>(()=> {
  return discountCBraw.value.map((i: DiscountQuantityBreakApiResponse) => {

    return {
      ...i,
      // if we reach the step by combining whats in the cart and whats about to be added
      reached: (totalQuantity.value + cartStore.getQuantityFromPartNo(props.activeVariant.partNo)) >= i.quantity,
      areaPrice: convertPackagePriceToAreaPrice(globalContent.getDisplayIncVat ? i.price : i.priceBeforeVat),
      unitDisplay,
      areaPerPackage: areaPerPackage.value,
    };
  });
});

const reachedDiscountPercent = computed(()=> {
  const percent = discountQuantityBreaks.value.filter(f => f.reached === true).map(m => m.discountPercentage);
  percent.push(0);
  return Math.max(...percent);
});

const reachedDiscountPrice = computed(()=> {
  uiStore.overrideDisplayPrice = -1;
  const reached = discountQuantityBreaks.value.filter(f => f.reached === true).map(m => parseFloat(globalContent.getDisplayIncVat ? m.price : m.priceBeforeVat));
  if (!reached.length) {
    return null;
  }

  const newPrice = Math.min(...reached);
  uiStore.overrideDisplayPrice = newPrice;
  return newPrice;
});

const palletQuantityInfo = computed(() => {
  const palletAmount = props.activeVariant.parametricByCode('ItemPalletAmount');
  const palletAreaM2 = props.activeVariant.parametricByCode('ItemAreaPerPalletM2');

  if (palletAmount?.value && palletAreaM2?.value) {
    let areaValue;

    if (globalContent.getMeasurement === 'imperial') {
      areaValue = palletAreaM2.value * 10.7639; // convert to ft²
    } else {
      areaValue = palletAreaM2.value;
    }

    return {
      packages: `${palletAmount.value} pcs`,
      area: `${areaValue.toFixed(2)} ${unitDisplay}`,
    };
  }

  return null;
});

onMounted(()=> {
  checkDiscountLevels();
});

/**
 * Samples are loaded on demand when the user opens the sample quickbuy.
 */
const samplesHydrated = ref<ProductModel[]>([]);
const marketSample = ref<ProductModel>();
const showSampleQuickbuy = ref(false);
const hasLoadedSamples = ref(false);
const isLoadingSamples = ref(false);
const hasSampleRelations = computed(() => {
  return props.activeVariant.relatedProducts.some(f => f.code === NorceRelatedProductTypeCodes.Samples);
});
const loadSamples = async() => {
  if (hasLoadedSamples.value || isLoadingSamples.value) {
    return;
  }
  isLoadingSamples.value = true;
  const samples = props.activeVariant.relatedProducts.filter(f => f.code === NorceRelatedProductTypeCodes.Samples).map(m => m.partNo);
  if (samples.length) {
    const res = await getExactVariantByPartNo(samples);
    if (res) {
      samplesHydrated.value = res.hits.map(hit => Product.create([hit]));
      if (globalContent.getIsB2c) {
        if (globalContent.domainMap?.countryCode === 'gb') {
          // For UK market, use ExtendedAssortment product
          marketSample.value = samplesHydrated.value?.find((s) => s.type === NorceProductTypes.ExtendedAssortment);
        } else if (globalContent.domainMap?.countryCode === 'us') {
          marketSample.value = samplesHydrated.value?.find((s) => s.partNo?.toUpperCase().startsWith('US01'));
        } else {
          marketSample.value = samplesHydrated.value?.find((s) => s.partNo?.toUpperCase().startsWith('SA01'));
        }
      } else {
        marketSample.value = samplesHydrated.value[0];
      }
    } else {
      console.log(lastError.value);
      console.log(lastErrorCode.value);
    }
  }
  hasLoadedSamples.value = true;
  isLoadingSamples.value = false;
};

const openSampleQuickbuy = async() => {
  await loadSamples();
  if (samplesHydrated.value.length) {
    showSampleQuickbuy.value = true;
  }
};

const scrollToAnchor = (id: string) => {
  const element = document.getElementById(id);
  console.log(element);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.scrollY - 80;
    console.log(elementPosition);
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth',
    });
  }
};

const existingAnchors = ref<string[]>([]);

onMounted(() => {
  checkAnchors();

  // we do a second check if some component loader really slow
  setTimeout(()=> checkAnchors(), 1000);
});

const checkAnchors = () => {
  const anchors = document.querySelectorAll('[id]');
  anchors.forEach((anchor) => {
    const id = anchor.getAttribute('id');
    if (id && !existingAnchors.value.includes(id)) {
      existingAnchors.value.push(id);
    }
  });
};

const hasRequestInstallation = computed(() => {
  return existingAnchors.value.includes('RequestInstallation');
});

</script>

<style lang="postcss">
.quantity-input {
  appearance: textfield;
}

.quantity-input::-webkit-inner-spin-button,
.quantity-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

@media (pointer: fine) {
  .quantity-input::-webkit-inner-spin-button,
  .quantity-input::-webkit-outer-spin-button {
    @apply opacity-100;
  }
}
</style>
